import { useState } from "react";
import { t } from "../lib/i18n";
import type { Diagram } from "@jomcerdik/shared";

/** A picture that belongs to the paper; if the file is missing the question stays answerable and says the picture is in the PDF. */
function PaperImage({ src }: { src: string }) {
  const [bad, setBad] = useState(false);
  return bad ? <p className="my-2 text-xs text-slate-500">{t("Gambar soalan ini ada dalam PDF kertas.")}</p> : <img src={src} alt="" onError={() => setBad(true)} className="mx-auto my-3 max-h-48 rounded-2xl bg-white object-contain" />;
}

/** On-screen version of a UASA paper's structured diagrams (the PDF draws the same data with pdf-lib). */
type Illustration = { type: "illustration"; illustration: { description?: string; asset?: { id?: string } } };
export function UasaDiagram({ d, examId }: { d: Diagram | Illustration; examId?: string }) {
  if (d.type === "illustration") {
    const id = d.illustration.asset?.id;
    return examId && id ? <PaperImage src={`/api/exams/${examId}/images/${id}`} /> : null;
  }
  switch (d.type) {
    case "table":
      return (
        <div className="my-3 overflow-x-auto">
          <table className="mx-auto border-collapse text-center text-base text-slate-900">
            <thead><tr>{d.headers.map((h, i) => <th key={i} className="border border-slate-400 bg-white/80 px-3 py-1.5 font-black">{h}</th>)}</tr></thead>
            <tbody>{d.rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} className="border border-slate-400 bg-white/60 px-3 py-1.5">{c}</td>)}</tr>)}</tbody>
          </table>
        </div>
      );
    case "bar": {
      const max = Math.max(...d.bars.map((b) => b.value), 1);
      const W = 320, H = 180, L = 36, B = 30, bw = (W - L - 10) / d.bars.length;
      const ticks = 4;
      return (
        <svg viewBox={`0 0 ${W} ${H}`} className="mx-auto my-3 w-full max-w-md" role="img" aria-label={d.yLabel ?? "carta palang"}>
          {Array.from({ length: ticks + 1 }, (_, i) => { const y = 8 + ((H - B - 8) * (ticks - i)) / ticks; return <g key={i}><line x1={L} x2={W - 4} y1={y} y2={y} stroke="#cbd5e1" /><text x={L - 4} y={y + 3} textAnchor="end" fontSize="9" fill="#475569">{Math.round((max * i) / ticks)}</text></g>; })}
          {d.bars.map((b, i) => { const h = ((H - B - 8) * b.value) / max; return <g key={i}><rect x={L + i * bw + bw * 0.18} y={H - B - h} width={bw * 0.64} height={h} rx="3" fill="#60a5fa" /><text x={L + i * bw + bw / 2} y={H - B + 13} textAnchor="middle" fontSize="9" fill="#0f172a">{b.label}</text></g>; })}
          {d.yLabel && <text x="4" y="8" fontSize="8" fill="#475569">{d.yLabel}</text>}
          {d.xLabel && <text x={W / 2} y={H - 3} textAnchor="middle" fontSize="8" fill="#475569">{d.xLabel}</text>}
        </svg>
      );
    }
    case "clock": {
      const hA = ((d.hour % 12) + d.minute / 60) * 30, mA = d.minute * 6;
      const pt = (a: number, r: number) => [60 + r * Math.sin((a * Math.PI) / 180), 60 - r * Math.cos((a * Math.PI) / 180)] as const;
      const [hx, hy] = pt(hA, 28), [mx, my] = pt(mA, 42);
      return (
        <svg viewBox="0 0 120 120" className="mx-auto my-3 h-36 w-36" role="img" aria-label={`${d.hour}:${String(d.minute).padStart(2, "0")}`}>
          <circle cx="60" cy="60" r="55" fill="#fff" stroke="#334155" strokeWidth="3" />
          {Array.from({ length: 12 }, (_, i) => { const [x, y] = pt(i * 30, 44); const [x2, y2] = pt(i * 30, 51); return <g key={i}><line x1={x2} y1={y2} x2={pt(i * 30, 55)[0]} y2={pt(i * 30, 55)[1]} stroke="#334155" /><text x={x} y={y + 4} textAnchor="middle" fontSize="11" fontWeight="700" fill="#0f172a">{i === 0 ? 12 : i}</text></g>; })}
          <line x1="60" y1="60" x2={hx} y2={hy} stroke="#0f172a" strokeWidth="4" strokeLinecap="round" />
          <line x1="60" y1="60" x2={mx} y2={my} stroke="#0f172a" strokeWidth="2.5" strokeLinecap="round" />
          <circle cx="60" cy="60" r="3" fill="#0f172a" />
        </svg>
      );
    }
    case "numberline": {
      const n = Math.round((d.to - d.from) / d.step), W = 320, L = 16, sw = (W - 2 * L) / n;
      return (
        <svg viewBox={`0 0 ${W} 60`} className="mx-auto my-3 w-full max-w-md" role="img" aria-label="garis nombor">
          <line x1={L} x2={W - L} y1="24" y2="24" stroke="#334155" strokeWidth="2" />
          {Array.from({ length: n + 1 }, (_, i) => { const x = L + i * sw; const v = d.from + i * d.step; const mk = d.marks?.find((m) => m.value === v); return <g key={i}><line x1={x} x2={x} y1="18" y2="30" stroke="#334155" strokeWidth="2" />{n <= 12 || i % Math.ceil(n / 10) === 0 ? <text x={x} y="46" textAnchor="middle" fontSize="9" fill="#0f172a">{Number.isInteger(v) ? v : +v.toFixed(2)}</text> : null}{mk && <><circle cx={x} cy="24" r="5" fill="#f97316" /><text x={x} y="12" textAnchor="middle" fontSize="10" fontWeight="700" fill="#c2410c">{mk.label}</text></>}</g>; })}
        </svg>
      );
    }
    case "fraction": {
      const cells = Array.from({ length: d.parts }, (_, i) => i < d.shaded);
      if (d.shape === "circle") {
        const r = 46, c = 50;
        return (
          <svg viewBox="0 0 100 100" className="mx-auto my-3 h-32 w-32" role="img" aria-label={`${d.shaded}/${d.parts}`}>
            {cells.map((on, i) => { const a0 = (i / d.parts) * 2 * Math.PI - Math.PI / 2, a1 = ((i + 1) / d.parts) * 2 * Math.PI - Math.PI / 2; const big = a1 - a0 > Math.PI ? 1 : 0; const p = (a: number) => `${c + r * Math.cos(a)} ${c + r * Math.sin(a)}`; return <path key={i} d={d.parts === 1 ? `M ${c - r} ${c} a ${r} ${r} 0 1 0 ${2 * r} 0 a ${r} ${r} 0 1 0 ${-2 * r} 0` : `M ${c} ${c} L ${p(a0)} A ${r} ${r} 0 ${big} 1 ${p(a1)} Z`} fill={on ? "#fbbf24" : "#fff"} stroke="#334155" strokeWidth="2" />; })}
          </svg>
        );
      }
      return (
        <svg viewBox="0 0 200 50" className="mx-auto my-3 w-full max-w-xs" role="img" aria-label={`${d.shaded}/${d.parts}`}>
          {cells.map((on, i) => <rect key={i} x={5 + (i * 190) / d.parts} y="8" width={190 / d.parts} height="34" fill={on ? "#fbbf24" : "#fff"} stroke="#334155" strokeWidth="2" />)}
        </svg>
      );
    }
    case "shapes": {
      const pts = d.shapes.flatMap((s) => s.points);
      const xs = pts.map((p) => p[0]), ys = pts.map((p) => p[1]);
      const x0 = Math.min(...xs), y0 = Math.min(...ys), w = Math.max(...xs) - x0 || 1, h = Math.max(...ys) - y0 || 1;
      const pad = 10, k = Math.min(260 / w, 160 / h);
      const X = (x: number) => pad + (x - x0) * k, Y = (y: number) => pad + (y - y0) * k;
      return (
        <svg viewBox={`0 0 ${w * k + 2 * pad} ${h * k + 2 * pad}`} className="mx-auto my-3 w-full max-w-xs" role="img" aria-label="bentuk">
          {d.shapes.map((s, i) => <g key={i}><polygon points={s.points.map((p) => `${X(p[0])},${Y(p[1])}`).join(" ")} fill="#bfdbfe" fillOpacity=".6" stroke="#334155" strokeWidth="2" />{s.label && <text x={X(s.points.reduce((a, p) => a + p[0], 0) / s.points.length)} y={Y(s.points.reduce((a, p) => a + p[1], 0) / s.points.length)} textAnchor="middle" fontSize="11" fontWeight="700" fill="#0f172a">{s.label}</text>}</g>)}
          {d.labels?.map((l, i) => <text key={i} x={X(l.x)} y={Y(l.y)} fontSize="11" fill="#0f172a">{l.text}</text>)}
        </svg>
      );
    }
    case "box":
      return <p className="my-3 rounded-2xl border-2 border-slate-400 bg-white/80 p-3 text-base text-slate-900" dir="auto">{d.textMs}</p>;
    default:
      return null;
  }
}
